import { useEffect, useState } from "react";
import shared from "../../data/js/shared";
import { dec2, googleEvent, name } from "../../utils/dom";
import { linkifyText } from "../../utils/linkify-text";
import { trackError, ErrorSeverity } from "../../utils/error-tracking";
import { calculateStripeFee } from "../../utils/stripe-fees";
import { LISTED, CHATTING, HELD, SOLD } from "../../data/js/listing-statuses";
import { canAcceptPayments } from "../../data/js/trust-levels";
import {
  DATA_SOURCE,
  isAdoptionListing,
} from "../../data/js/data-sources";
import Currency from "../currency";
import FormatPrice from "../format-price";
import EmailParser from "../email-parser";
import { useProduct } from "../../contexts";
import { fetchListingRemoteUrl } from "../../utils/fetch-listing-remote-url";
import { hardNavigate } from "../../utils/hard-navigate";

const {
  GCCIntro,
  MYEVIntro,
  GCC_DATASOURCE,
  MYEV_DATASOURCE,
  LOTLINX_DATASOURCE,
  AMAZON_DATASOURCE,
  BARGAINHUNTER_DATASOURCE,
  SPORTSERVER_DATASOURCE,
  emailOnlyLabel,
  cancelLabel,
  contactShelterHeading,
  emailPreferred,
  emailSubject,
  clickForFees,
} = shared.product;

interface UseProductDetailStateParams {
  product: Record<string, unknown> | null;
  profile: Record<string, unknown> | null;
  allChats: Record<string, unknown>[];
  /** Reads the chat list into `allChats` (the chat context's getAllChats). */
  loadChats: () => Promise<unknown>;
  setPopupContents: (content: React.ReactNode) => void;
  setPopupOpen: (open: boolean) => void;
}

export function useProductDetailState({
  product,
  profile,
  allChats,
  loadChats,
  setPopupContents,
  setPopupOpen,
}: UseProductDetailStateParams) {
  const [appliedCoupon, setAppliedCoupon] = useState<{
    couponId: number;
    code: string | null;
    name: string;
    discountAmount: number;
    discountType: string;
    discountValue: number;
  } | null>(null);
  const [couponChecked, setCouponChecked] = useState(true);

  // Read the currently-selected CJ variant from ProductProvider so the
  // displayed price tracks the picker. `null` for non-CJ / no-variant
  // products — falls back to the listing's base cost/price below.
  const { selectedVariant } = useProduct();

  const {
    id = "",
    title = "",
    text = "",
    price = 0,
    cost = 0,
    slashedPrice,
    percentOff,
    height = "",
    width = "",
    depth = "",
    weight = "",
    itemCondition = null,
    vin = "",
    mileage = "",
    user = { id: "" },
    photos = [],
    categoryGroupId = "",
    interest_count = 0,
    dataSourceId,
    remote_url,
    tag = "",
  } = (product || {}) as Record<string, unknown>;

  const stripeFee = dec2(calculateStripeFee(price as number));
  // Use username if available, otherwise fall back to ID
  const userRecord = user as Record<string, unknown>;
  const slug = userRecord?.username || userRecord?.id || "";

  const productDataSourceId = parseInt(String(dataSourceId));
  const isGCCorMyEV =
    productDataSourceId === GCC_DATASOURCE ||
    productDataSourceId === MYEV_DATASOURCE;

  // Check if user owns this listing and has no existing chat
  const isOwnListing =
    userRecord?.id && profile?.id && String(userRecord.id) === String(profile.id);
  const listingChats = allChats.filter(
    (chat: Record<string, unknown>) => String(chat.listingId) === String(id),
  );
  const hasExistingChat = listingChats.length > 0;
  // The chat button on your OWN listing cannot open the chat window. The
  // window holds one thread and looks it up by (listing, you, the listing's
  // seller), and on your own listing the seller is you: the lookup never
  // matched, the window came up empty, and a reply sent from it went to
  // whichever buyer had the latest order. A listing can have a thread per
  // buyer, so the button goes to My Chats instead: straight into the thread
  // when there is only one, or the list when there are several.
  const ownListingChats = isOwnListing ? listingChats : [];
  const onlyChatId = ownListingChats.length === 1 ? ownListingChats[0].id : null;
  const ownListingChatUrl: string | null =
    ownListingChats.length === 0
      ? null
      : onlyChatId
        ? `/account/chats/${String(onlyChatId)}`
        : "/account/chats";

  // Nothing else fills the chat list on this page: only My Chats, a send and
  // a delete read it. A seller who hard-loaded their own listing, or opened it
  // in a new tab, had an empty list, so the button stayed hidden even with a
  // buyer waiting. Read it here, once per own listing shown (getAllChats is
  // a stable callback, so it does not re-run the read).
  const ownListingId = isOwnListing && id != null ? String(id) : null;
  useEffect(() => {
    if (ownListingId) void loadChats();
  }, [ownListingId, loadChats]);

  // Partner listing check
  const isPartnerListing =
    productDataSourceId === LOTLINX_DATASOURCE ||
    productDataSourceId === AMAZON_DATASOURCE ||
    productDataSourceId === BARGAINHUNTER_DATASOURCE ||
    productDataSourceId === SPORTSERVER_DATASOURCE ||
    productDataSourceId === DATA_SOURCE.RESCUEGROUPS;
  const shouldHideChatButton =
    (isOwnListing && !hasExistingChat) || isGCCorMyEV || isPartnerListing;

  // Availability and buyability
  const productRecord = product as Record<string, unknown>;
  const isAvailable =
    productRecord?.status === LISTED ||
    productRecord?.status === String(LISTED) ||
    productRecord?.status === CHATTING ||
    productRecord?.status === String(CHATTING);
  const isPendingOrSold =
    productRecord?.status === HELD ||
    productRecord?.status === String(HELD) ||
    productRecord?.status === SOLD ||
    productRecord?.status === String(SOLD);
  // Owners see the same order breakdown a buyer sees — same subtotal, shipping,
  // fee and total. Only the button differs (disabled, handled in the pricing
  // component), so `isOwnListing` deliberately does NOT gate this.
  const isBuyable =
    !isGCCorMyEV &&
    !isPartnerListing &&
    isAvailable &&
    canAcceptPayments((user as Record<string, unknown>)?.trustLevel as number);

  // CJ variant override: when a variant is picked, its price is the source
  // of truth (matches what create-payment-intent will charge server-side).
  const baseCurrentPrice = (cost || price) as number;
  const currentPrice = selectedVariant ? selectedVariant.price : baseCurrentPrice;
  const priceInfo = !isAdoptionListing(dataSourceId as number | undefined | null)
    ? Currency(currentPrice, FormatPrice)
    : clickForFees;

  // Email button (for GCC/MYEV)
  const footer =
    ((product as Record<string, unknown>)?.remote_url as string) || "";
  const parts = footer.split("/").slice(3);
  const productDataSource = parseInt(String(product?.dataSourceId));
  const email =
    productDataSource === MYEV_DATASOURCE
      ? "support@myev.com"
      : "info@gatewayclassiccars.com";
  const mailto = encodeURIComponent("mailto:Seller <" + email + ">");

  const body = encodeURIComponent(
    productDataSource === MYEV_DATASOURCE
      ? MYEVIntro + footer + "\n" + parts.join(" ")
      : GCCIntro + footer + "\n" + parts.join(" "),
  );

  const subject = encodeURIComponent(
    emailSubject + product?.title + " " + parts.join(" "),
  );
  const href = "mailto:" + mailto + "?subject=" + subject + "&body=" + body;

  const clickCancelButton = (e: React.MouseEvent) => {
    e.preventDefault();
    setPopupOpen(false);
    setPopupContents("");
    googleEvent("email_only_popup_cancel", { ...product });
  };

  const clickEmailButton = () => {
    googleEvent("email_only_popup_open", { ...product });
    setPopupContents(
      <div className="email-only-button">
        <div className="email-contact">
          <div className="headsUp">{emailPreferred}</div>
          <div className="vertical-buttons">
            <a href={href} className="greenButton">
              {emailOnlyLabel}
            </a>
            <a href="#" className="hollowButton" onClick={clickCancelButton}>
              {cancelLabel}
            </a>
          </div>
        </div>
      </div>,
    );
    setPopupOpen(true);
  };

  const getContact = (contactText: string) => {
    const st = contactText.split("For more information, please contact us:");
    if (st.length == 2) {
      return <EmailParser text={st[1]} />;
    }
  };

  const openRemoteUrlButton = async () => {
    const pdSourceId = parseInt(
      String((product as Record<string, unknown>)?.dataSourceId),
    );
    const field = pdSourceId === AMAZON_DATASOURCE ? "videoUrl" : "remote_url";
    const url = (product as Record<string, unknown>)?.[field] as string;

    if (url) {
      googleEvent("external_partner_link_open", { ...product });
      window.open(url, "_blank");
      return;
    }

    // The click-out URL is missing from the page payload. Product pages are
    // statically rendered, so a page built before the listing's metadata row
    // carried a URL keeps serving a button that goes nowhere. Open the tab now,
    // while the user's gesture is still live, then resolve the URL against the
    // database and point the tab at it.
    const pendingWindow = window.open("", "_blank");
    const listingId = parseInt(String(id));
    const recoveredUrl = await fetchListingRemoteUrl(listingId);

    if (recoveredUrl) {
      googleEvent("external_partner_link_open", { ...product });
      if (pendingWindow) {
        pendingWindow.location.href = recoveredUrl;
      } else {
        // A popup blocker refused the tab: go there in this one.
        hardNavigate(recoveredUrl);
      }
      return;
    }

    pendingWindow?.close();
    // Nothing on the page and nothing in the database, so this is a dead button
    // for a real user. `hasRemoteUrl`/`hasVideoUrl` separate the two ways that
    // happens: a feed that stopped sending urls, or one whose url arrived under
    // the other key — Amazon reads videoUrl, every other source reads
    // remote_url. The message stays fixed so the cluster does not fragment.
    trackError("product-detail-no-remote-url", ErrorSeverity.WARNING, {
      listing_id: Number.isFinite(listingId) ? listingId : undefined,
      component: "useProductDetailState",
      action: "openRemoteUrlButton",
      extra: {
        dataSourceId: Number.isNaN(pdSourceId) ? null : pdSourceId,
        field,
        hasRemoteUrl: Boolean((product as Record<string, unknown>)?.remote_url),
        hasVideoUrl: Boolean((product as Record<string, unknown>)?.videoUrl),
        status: (product as Record<string, unknown>)?.status ?? null,
      },
    });
  };

  const contactShelterButton = () => {
    const contactText = getContact(
      (product as Record<string, unknown>).text as string,
    );
    googleEvent("contact_shelter_popup_open", { ...product });
    setPopupContents(
      <div className="contact-info more-description">
        <h3>{contactShelterHeading}</h3>
        <div className="scrollable">
          {contactText ? (
            contactText
          ) : (
            <div className="description-text whitespace-pre-wrap select-text">
              {linkifyText(
                (product as Record<string, unknown>).text as string,
                "contact-link",
              )}
            </div>
          )}
        </div>
      </div>,
    );
    setPopupOpen(true);
  };

  const DESC_LENGTH = 400;

  const shorter = (shorterText: string, maxLength: number = DESC_LENGTH) => {
    if (shorterText.length <= maxLength) {
      return shorterText;
    }
    const elps = "...";
    const lastSpace = shorterText.lastIndexOf(" ", maxLength);
    if (lastSpace === -1) {
      return shorterText.slice(0, maxLength) + elps;
    }
    return shorterText.slice(0, lastSpace) + elps;
  };

  // Coupon: auto-apply from search results
  useEffect(() => {
    if (!product) return;
    const p = product as Record<string, unknown>;
    if (p.couponId && p.couponDiscountAmount) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs the auto-applied coupon (and couponChecked) from search-result product data when the product changes; checkout pricing depends on this exact sequencing
      setAppliedCoupon({
        couponId: p.couponId as number,
        code: (p.couponCode as string | null) || null,
        name: (p.couponName as string) || "Coupon",
        discountAmount: p.couponDiscountAmount as number,
        discountType: (p.couponDiscountType as string) || "fixed",
        discountValue:
          (p.couponDiscountValue as number) ||
          (p.couponDiscountAmount as number),
      });
      setCouponChecked(true);
    } else {
      setAppliedCoupon(null);
    }
  }, [product?.id, product]);

  // Auto-apply coupons use the main checkout page
  const checkoutUrl =
    appliedCoupon && couponChecked ? `/checkout/${id}` : undefined;

  const userName = name(user);

  // Helper function to get carrier logo
  const getCarrierLogo = (carrier: string) => {
    const carrierUpper = carrier.toUpperCase();

    if (carrierUpper.includes("USPS")) {
      return "/images/shippers/usps-logo.png";
    } else if (carrierUpper.includes("UPS")) {
      return "/images/shippers/ups-logo.svg";
    } else if (carrierUpper.includes("FEDEX")) {
      return "/images/shippers/fedex-logo.png";
    } else if (carrierUpper.includes("YUNEXPRESS")) {
      return "/images/shippers/yunexpress-logo.png";
    } else if (carrierUpper.includes("LUWEI")) {
      return "/images/shippers/luwei-logo.png";
    } else if (carrierUpper.includes("4PX")) {
      return "/images/shippers/4px-logo.png";
    } else if (carrierUpper.includes("CJPACKET") || carrierUpper.startsWith("CJ ")) {
      return "/images/shippers/cj-logo.svg";
    }

    return null;
  };

  // Helper function to render carrier logo
  const renderCarrierLogo = (carrier: string) => {
    const logoSrc = getCarrierLogo(carrier);

    if (!logoSrc) return null;

    // `carrier-logo` sizes by HEIGHT, in the stylesheet. Constraining the width
    // instead made the chip a different height for every carrier, because the
    // logos are not the same shape: UPS is 23x27 and came out 63px tall, the
    // 120x120 square ones 56px, USPS 48px, FedEx 31px — inside a 36px price
    // row. So the Shipping row's height depended on who was shipping.
    return (
      <span className="price-row__carrier">
        <img src={logoSrc} alt={carrier} />
      </span>
    );
  };

  return {
    // Destructured product fields
    id,
    title,
    text,
    price,
    cost,
    slashedPrice,
    percentOff,
    height,
    width,
    depth,
    weight,
    itemCondition,
    vin,
    mileage,
    user,
    photos,
    categoryGroupId,
    interest_count,
    dataSourceId,
    remote_url,
    tag,
    // Computed state
    stripeFee,
    slug,
    productDataSourceId,
    isGCCorMyEV,
    isOwnListing,
    hasExistingChat,
    ownListingChats,
    ownListingChatUrl,
    isPartnerListing,
    shouldHideChatButton,
    isAvailable,
    isPendingOrSold,
    isBuyable,
    currentPrice,
    priceInfo,
    checkoutUrl,
    userName,
    DESC_LENGTH,
    // Actions
    clickEmailButton,
    contactShelterButton,
    openRemoteUrlButton,
    shorter,
    getCarrierLogo,
    renderCarrierLogo,
  };
}
